import * as React from "react";

import { getNumberSeparators, useNumberFormatLocale } from "@/common/helpers/number-format";
import { NumericFormat } from "react-number-format";

import { Input, type InputProps } from "./input";

export interface LocalizedNumberInputProps extends Omit<InputProps, "defaultValue" | "onChange" | "type" | "value"> {
  value?: string;
  onValueChange: (value: string) => void;
  locale?: string;
  allowNegative?: boolean;
  integerOnly?: boolean;
  maxFractionDigits?: number;
}

interface NumericFormatInputProps extends InputProps {
  inputPrefix?: React.ReactNode;
}

function shouldAllowNegativeNumbers(explicitAllowNegative: boolean | undefined, min: InputProps["min"]): boolean {
  if (explicitAllowNegative !== undefined) {
    return explicitAllowNegative;
  }

  if (min == null || min === "") {
    return true;
  }

  const parsedMin = typeof min === "number" ? min : Number(min);
  return !Number.isFinite(parsedMin) || parsedMin < 0;
}

const NumericFormatInput = ({ inputPrefix, ref, ...props }: NumericFormatInputProps) => (
  <Input {...props} ref={ref} type="text" prefix={inputPrefix} />
);

NumericFormatInput.displayName = "NumericFormatInput";

const LocalizedNumberInput = ({
  value = "",
  onValueChange,
  locale,
  allowNegative,
  integerOnly = false,
  maxFractionDigits,
  inputMode,
  prefix,
  onChangeCapture,
  ref,
  ...props
}: LocalizedNumberInputProps) => {
  const lastInputValueRef = React.useRef("");
  const preferredLocale = useNumberFormatLocale();
  const resolvedLocale = locale ?? preferredLocale;
  const { decimalSeparator, groupSeparator } = React.useMemo(
    () => getNumberSeparators(resolvedLocale),
    [resolvedLocale],
  );
  const resolvedAllowNegative = shouldAllowNegativeNumbers(allowNegative, props.min);

  return (
    <NumericFormat
      {...props}
      customInput={NumericFormatInput}
      getInputRef={ref}
      value={value}
      valueIsNumericString
      allowNegative={resolvedAllowNegative}
      decimalScale={integerOnly ? 0 : maxFractionDigits}
      decimalSeparator={decimalSeparator}
      thousandSeparator={groupSeparator}
      inputPrefix={prefix}
      inputMode={inputMode ?? (integerOnly ? "numeric" : "decimal")}
      onChangeCapture={(event) => {
        lastInputValueRef.current = event.currentTarget.value;
        onChangeCapture?.(event);
      }}
      onValueChange={({ value: nextValue }, sourceInfo) => {
        if (sourceInfo.source === "event") {
          // react-number-format strips a disallowed minus before emitting; keep the raw change value to reject it.
          const inputValue = lastInputValueRef.current;
          lastInputValueRef.current = "";

          if (!resolvedAllowNegative && inputValue.includes("-")) {
            return;
          }

          onValueChange(nextValue);
        }
      }}
    />
  );
};

LocalizedNumberInput.displayName = "LocalizedNumberInput";

export { LocalizedNumberInput };
